import { Container, Divider, Grid, Typography } from "@mui/material";
import BaseTitle from "../../../common/base-title/base-title";
import { ArrowLeft2 } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../../../utils/utility";
import useUserInfo from "../../../../hooks/useUserInfo";
import { useContext, useEffect, useState } from "react";
import { BaseButton } from "../../../common/base-button/base-button";
import useAutomaticReserveBooking from "../../../../hooks/useAutomaticReserveBooking";
import { useNavigate } from "react-router";
import { LocaleContext } from "../../../../routing/LangRouter";
import BaseTextField from "../../../common/base-text-field/base-text-field";
import { connect } from "react-redux";
import { passengersToPersonCounter } from "../../../../utils/passengersToPersonCounter";
import { getFlightAutomaticReservationPageUrl } from "../../../../routing/routingConstants/AppUrls";
import MobileHeader from "../../../common/mobile-header/mobile-header";

function AutomaticReservePrice({ selectedTicket }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);

    const { automaticReserveBooking } = useAutomaticReserveBooking();

    //request to get user information
    const { userInformation, getUserInfo } = useUserInfo();

    useEffect(() => {
        getUserInfo();
    }, []);

    const userBalance = Math.max(0, userInformation?.balance) || 0;

    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation")) || {};
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation")) || {};
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight")) || {};
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight")) || {};
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const selectedReservation = JSON.parse(sessionStorage.getItem("selectedReservation")) || {};

    const { isFlightInternational } = generalInformation;
    const { adult_price, cobin, tag, aircraft, airline_id } = selectedFlight;
    const ticketAdultPrice = adult_price ?? selectedIntFlight?.fares?.adult?.price;
    const airlineId = airline_id ?? selectedIntFlight?.segments?.[0]?.airline?.id;
    const isSelectedReserve = Object.keys(selectedReservation).length > 0;

    const [percentageValue, setPercentageValue] = useState(null);
    const [adultPrice, setAdultPrice] = useState(ticketAdultPrice);

    const { personCounter } = passengersToPersonCounter?.(selectedReservation?.item?.passengers, selectedReservation);

    const adultCount = isSelectedReserve
        ? personCounter?.adult
        : (domesticFlightInformation?.personCounter?.adult ?? intFlightInformation?.personCounter?.adult);
    const childCount = isSelectedReserve
        ? personCounter?.child
        : (domesticFlightInformation?.personCounter?.child ?? intFlightInformation?.personCounter?.child);
    const infantCount = isSelectedReserve
        ? personCounter?.infant
        : (domesticFlightInformation?.personCounter?.infant ?? intFlightInformation?.personCounter?.infant);

    const maxPrice = selectedFlight?.max_price ?? selectedIntFlight?.max_price;
    const totalPrice = adultCount * (maxPrice || adultPrice) + childCount * (maxPrice || adultPrice) + infantCount * (maxPrice || adultPrice);
    const amountToPay = Math.max(0, totalPrice - userBalance);
    const enoughBalance = userBalance >= totalPrice;

    const cabinId = cobin === "Economy" ? 2 : 3;

    useEffect(() => {
        if (!!ticketAdultPrice) {
            if (isFlightInternational) {
                sessionStorage.setItem(
                    "selectedIntFlight",
                    JSON.stringify({
                        ...selectedIntFlight,
                        max_price: adultPrice,
                        book_until_unit: 1,
                        cabin_type: cabinId,
                        airlines: tag === "ch118" ? [aircraft] : [airlineId],
                    }),
                );
            } else {
                sessionStorage.setItem(
                    "selectedFlight",
                    JSON.stringify({
                        ...selectedFlight,
                        max_price: adultPrice,
                        book_until_unit: 1,
                        cabin_type: cabinId,
                        airlines: tag === "ch118" ? [aircraft] : [airlineId],
                    }),
                );
            }
        }
    }, []);

    const handleBooking = () => {
        if (!enoughBalance) {
            navigate(`/${locale}/user-panel/wallet`);
        } else {
            automaticReserveBooking();
        }
    };

    const renderPriceRow = (passengerType, count, price) => {
        if (count <= 0) return null;

        return (
            <Grid container item xs={12} justifyContent="space-between">
                <span>
                    {count} {t(passengerType)}:
                </span>
                <span>{`${numberWithCommas(price * count)} ${t("currency")}`}</span>
            </Grid>
        );
    };

    const renderPrice = (title, price) => {
        return (
            <Grid container item xs={12} justifyContent="space-between">
                <span>{title}:</span>
                <span>
                    {numberWithCommas(price)} {t("currency")}
                </span>
            </Grid>
        );
    };

    const handlePercentageChange = event => {
        const inputElement = event.target.value;
        setPercentageValue(inputElement);
    };

    const percentageIncrement = (price, percentageValue) => {
        const pricePercentage = (percentageValue / 100) * price;
        const result = price + pricePercentage;
        setAdultPrice(result);

        isFlightInternational
            ? sessionStorage.setItem(
                  "selectedIntFlight",
                  JSON.stringify({
                      ...selectedIntFlight,
                      max_price: result,
                  }),
              )
            : sessionStorage.setItem(
                  "selectedFlight",
                  JSON.stringify({
                      ...selectedFlight,
                      max_price: result,
                  }),
              );
    };
    const handleGoBack = () => {
        navigate(getFlightAutomaticReservationPageUrl(locale));
    };

    return (
        <Container maxWidth="lg">
            <MobileHeader title={t("confirmInformation")} justifyContent="center" handleClick={handleGoBack} />
            <Grid container rowGap={3} sx={{ paddingTop: { xs: 3 }, paddingBottom: { xs: 3 } }}>
                <BaseTitle
                    title={
                        <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                            <ArrowLeft2 /> {t("General.Price Calculation")}
                        </Grid>
                    }
                    dividerColor="1px solid var(--medium-gray-color)"
                />
                <Grid
                    container
                    direction="row"
                    sx={{
                        justifyContent: "space-between",
                    }}
                >
                    <Grid md={5}>
                        <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
                            {t("Price.Calculation Based on Last Price")}
                        </Typography>
                    </Grid>

                    <Grid container item xs={12} rowGap={2} md={4}>
                        {selectedTicket && (
                            <Grid
                                container
                                direction="row"
                                sx={{
                                    alignItems: "center",
                                }}
                            >
                                <Grid container item md={9} sx={{ justifyContent: { md: "space-between" } }}>
                                    <Grid container item md={4} xs={4} alignItems="center">
                                        <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", md: "1rem" }, fontWeight: "bold" }}>
                                            {t("Price.Latest Price")} +
                                        </Typography>
                                    </Grid>

                                    <Grid container item md={8} xs={8}>
                                        <BaseTextField
                                            radius="10px"
                                            label={t("Price.Price Increase Percentage")}
                                            name="amount"
                                            value={percentageValue}
                                            setValue={handlePercentageChange}
                                            inputProps={{
                                                inputMode: "numeric",
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container item md={3} sx={{ pl: { md: 3 }, pt: { xs: 2, md: 0 } }}>
                                    <BaseButton
                                        text="محاسبه"
                                        color="primary"
                                        size="medium"
                                        radius="radius-10"
                                        handleClick={() => percentageIncrement(ticketAdultPrice, percentageValue)}
                                    />
                                </Grid>
                            </Grid>
                        )}
                        {renderPriceRow("adult", adultCount, maxPrice || adultPrice)}
                        {renderPriceRow("child", childCount, maxPrice || adultPrice)}
                        {renderPriceRow("infant", infantCount, maxPrice || adultPrice)}
                        <Divider width="100%" />
                        {renderPrice(t("total"), totalPrice)}
                        <Divider width="100%" />
                        {renderPrice(t("General.Your Credit"), userBalance)}
                        <Divider width="100%" />
                        {renderPrice(t("amountPayable"), amountToPay)}
                        <Grid container item xs={12} md={12} justifyContent="flex-end" pt={3}>
                            <Grid container item xs={12} md={6} justifyContent="flex-end">
                                <BaseButton
                                    text={enoughBalance ? t("confirmation") : t("Price.Wallet Top-Up And Reservation Confirmation")}
                                    color="primary"
                                    size="medium"
                                    radius="radius-10"
                                    handleClick={handleBooking}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
}

export default connect(mapStateToProps)(AutomaticReservePrice);
function mapStateToProps(state) {
    return {
        selectedTicket: state.selectedTicket,
    };
}
